import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Checkbox, Modal, Spin, message } from "antd"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { gen, getActingUserRequestFields, IClub, swimminglyApi } from "./utils"
import { IClubSeasonInformation } from "./MeetActionsModal"
import { updateSeasonMeetArrayListener } from "./MeetSchedulePageNew"

interface IUpdateMeetParticipatingClubsTraditionalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId: number
  homeClubId: number
  meetClubSeasons: IClubSeasonInformation[]
  refresh: () => void
}

const getClubLabel = (club: IClub) => club.nameLong || club.name || "Unnamed club"

const sectionTitleStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#595959",
  textTransform: "uppercase" as const,
  letterSpacing: 0.6,
}

const MEET_ACTIONS_CHILD_MODAL_Z_INDEX = 1100

export default function UpdateMeetParticipatingClubsTraditional({
  visible,
  setVisible,
  meetId,
  homeClubId,
  meetClubSeasons,
  refresh,
}: IUpdateMeetParticipatingClubsTraditionalProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const actingUser = aliasedUser || user
  const [availableClubs, setAvailableClubs] = useState<IClub[]>([])
  const [selectedClubIds, setSelectedClubIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const seasonId = meetClubSeasons[0]?.season?.seasonId
  const currentClubIds = useMemo(
    () =>
      meetClubSeasons
        .map((clubSeason) => clubSeason.club.clubId)
        .filter((clubId): clubId is number => Boolean(clubId)),
    [meetClubSeasons],
  )

  useEffect(() => {
    if (!visible) return
    setSelectedClubIds(currentClubIds)
  }, [visible, currentClubIds])

  useEffect(() => {
    if (!visible || !seasonId) return

    let mounted = true
    setLoading(true)

    swimminglyApi
      .get(gen(`/api/getClubsInSeason/${seasonId}`))
      .then((data: { status: string; clubs?: IClub[]; message?: string }) => {
        if (!mounted) return
        if (data.status !== "success" || !data.clubs) {
          message.error(data.message || "Problem loading clubs for this season.")
          setAvailableClubs([])
          return
        }
        setAvailableClubs(data.clubs)
      })
      .catch(() => {
        if (!mounted) return
        message.error("Problem loading clubs for this season.")
        setAvailableClubs([])
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [visible, seasonId])

  const clubsForSelection = useMemo(() => {
    const mergedClubs = [
      ...meetClubSeasons.map((clubSeason) => clubSeason.club),
      ...availableClubs,
    ]
    const uniqueClubs = new Map<number, IClub>()

    mergedClubs.forEach((club) => {
      if (!club.clubId || uniqueClubs.has(club.clubId)) return
      uniqueClubs.set(club.clubId, club)
    })

    return Array.from(uniqueClubs.values()).sort((left, right) =>
      getClubLabel(left).localeCompare(getClubLabel(right)),
    )
  }, [availableClubs, meetClubSeasons])

  const currentClubsForSelection = useMemo(
    () =>
      clubsForSelection.filter(
        (club) => !!club.clubId && currentClubIds.includes(club.clubId),
      ),
    [clubsForSelection, currentClubIds],
  )

  const availableClubsForSelection = useMemo(
    () =>
      clubsForSelection.filter(
        (club) => !club.clubId || !currentClubIds.includes(club.clubId),
      ),
    [clubsForSelection, currentClubIds],
  )

  const clubsToRemove = useMemo(
    () => currentClubIds.filter((clubId) => !selectedClubIds.includes(clubId)),
    [currentClubIds, selectedClubIds],
  )

  const closeModal = () => {
    if (saving) return
    setVisible(false)
  }

  const renderClubRow = (club: IClub) => {
    const clubId = club.clubId
    if (!clubId) return null

    const isHostClub = clubId === homeClubId

    return (
      <label
        key={clubId}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "10px 12px",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
        }}
      >
        <Checkbox
          checked={selectedClubIds.includes(clubId)}
          disabled={isHostClub}
          onChange={(event) => {
            setSelectedClubIds((previous) => {
              if (event.target.checked) {
                return previous.includes(clubId) ? previous : [...previous, clubId]
              }

              return previous.filter(
                (selectedClubId) => selectedClubId !== clubId,
              )
            })
          }}
        >
          {getClubLabel(club)}
        </Checkbox>
        {isHostClub ? (
          <span style={{ color: "#8c8c8c", fontSize: 12 }}>Host club</span>
        ) : null}
      </label>
    )
  }

  const submitChanges = () => {
    setSaving(true)

    const clubSeasonIdsToRefresh = Array.from(
      new Set(
        meetClubSeasons
          .map((clubSeason) => Number(clubSeason.clubSeasonId || 0))
          .filter((clubSeasonId) => clubSeasonId > 0),
      ),
    )

    const emitSeasonRefreshes = () => {
      clubSeasonIdsToRefresh.forEach((clubSeasonId) => {
        updateSeasonMeetArrayListener.emit(`updateSeasonMeets${clubSeasonId}`)
      })
    }

    swimminglyApi
      .post(gen("/api/updateMeetParticipatingClubs"))
      .body({
        meetId,
        clubIds: selectedClubIds,
        ...getActingUserRequestFields(actingUser),
      })
      .then((data: { status: string; message?: string }) => {
        if (data.status === "success") {
          message.success(
            data.message || "Meet participating clubs updated successfully.",
          )
          refresh()
          emitSeasonRefreshes()
          setVisible(false)
          return
        }

        if (data.status === "warning") {
          message.warning(
            data.message ||
              "Meet participating clubs updated, but SwimminglyFan sync failed.",
          )
          refresh()
          emitSeasonRefreshes()
          setVisible(false)
          return
        }

        message.error(data.message || "Problem updating participating clubs.")
      })
      .catch(() => {
        message.error("Problem updating participating clubs.")
      })
      .finally(() => setSaving(false))
  }

  const onSave = () => {
    if (selectedClubIds.length === 0) {
      message.error("At least one participating club is required.")
      return
    }

    if (clubsToRemove.length === 0) {
      submitChanges()
      return
    }

    Modal.confirm({
      title: "Remove participating clubs?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will remove the selected clubs from the meet. Clubs with entries, lineup confirmation, heat sheet acceptance, or declarations activity will be blocked.",
      okText: "Confirm changes",
      cancelText: "Cancel",
      zIndex: MEET_ACTIONS_CHILD_MODAL_Z_INDEX + 10,
      onOk: submitChanges,
    })
  }

  return (
    <Modal
      title="Update Participating Clubs"
      open={visible}
      zIndex={MEET_ACTIONS_CHILD_MODAL_Z_INDEX}
      onCancel={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={onSave} loading={saving}>
          Save
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Only clubs in this meet's season can participate. The host club stays locked until the host assignment changes."
      />

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16, maxHeight: 360, overflowY: "auto" }}>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={sectionTitleStyle}>Currently Entered In The Meet</div>
            {currentClubsForSelection.map(renderClubRow)}
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={sectionTitleStyle}>Not Currently Entered In The Meet</div>
            {availableClubsForSelection.length > 0 ? (
              availableClubsForSelection.map(renderClubRow)
            ) : (
              <div
                style={{
                  padding: "10px 12px",
                  border: "1px dashed #d9d9d9",
                  borderRadius: 8,
                  color: "#8c8c8c",
                  fontSize: 12,
                }}
              >
                No additional clubs are available in this season.
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}